import { useState } from 'react';
import { Button } from '@/ui/components/ui/button';
import { Label } from '@/ui/components/ui/label';
import type { ProjectObject } from '../../../hooks/use-admin';

interface HmacSecretSectionProps {
	project: ProjectObject;
	regenerateHmacSecret: (opts: {
		variables: { id: string };
	}) => Promise<unknown>;
	onSecretRevealed: () => void;
	canEdit: boolean;
}

export function HmacSecretSection({
	project,
	regenerateHmacSecret,
	onSecretRevealed,
	canEdit,
}: HmacSecretSectionProps) {
	const [visible, setVisible] = useState(false);
	const [copied, setCopied] = useState(false);
	const [regenerating, setRegenerating] = useState(false);

	const handleCopy = () => {
		if (!project.hmacSecret) return;
		navigator.clipboard.writeText(project.hmacSecret);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const handleRegenerate = async () => {
		setRegenerating(true);
		try {
			await regenerateHmacSecret({ variables: { id: project.id } });
			setVisible(true);
			onSecretRevealed();
		} finally {
			setRegenerating(false);
		}
	};

	return (
		<div className='flex flex-col gap-2'>
			<div>
				<Label>HMAC secret</Label>
				<p className='text-xs text-muted-foreground mt-0.5'>
					Used to sign embed requests from your server.
				</p>
			</div>

			{project.hmacSecret ? (
				<div className='flex items-center gap-2'>
					<code className='flex-1 rounded-md bg-muted px-3 py-2 text-xs font-mono truncate'>
						{visible
							? project.hmacSecret
							: '••••••••••••••••••••••••••••••••'}
					</code>
					<Button
						type='button'
						variant='outline'
						size='sm'
						onClick={() => setVisible((v) => !v)}
					>
						{visible ? 'Hide' : 'Show'}
					</Button>
					<Button
						type='button'
						variant='outline'
						size='sm'
						onClick={handleCopy}
					>
						{copied ? 'Copied!' : 'Copy'}
					</Button>
				</div>
			) : (
				<p className='text-sm text-muted-foreground'>
					No secret yet. Click "Generate secret" to create one.
				</p>
			)}

			{canEdit && (
				<div>
					<Button
						type='button'
						variant='outline'
						size='sm'
						disabled={regenerating}
						onClick={handleRegenerate}
					>
						{regenerating
							? 'Generating…'
							: project.hmacSecret
								? 'Regenerate secret'
								: 'Generate secret'}
					</Button>
				</div>
			)}
		</div>
	);
}
